import { AppLocation } from 'src/navigation/appLocations'
import * as Routes from 'src/navigation/urls'

/**
 * Organization-settings flat route tree (EX-39748) — the single source describing
 * the organization tabbed-family, mirroring the Budgets/Accounts route-tree pattern
 * (see budgetsRouteTree.ts / accountsRouteTree.ts) adapted to this family's real shape.
 *
 * This is the ONE place that enumerates the organization-settings routes. Everything
 * else derives from it:
 *   - the flat-route GATE paths (`organizationGatePaths`) registered once in
 *     flatArchitectureRoutes.tsx so the whole family resolves to one page,
 *   - the per-route METADATA fed to `locationHooks` (sidenav active state, breadcrumbs,
 *     content type) — built in `flatArchitectureRouteMetadata.ts`.
 *
 * THREE DELIBERATE DIFFERENCES FROM BUDGETS/ACCOUNTS (documented; see PR body):
 *   1. The organization IS the entity: every route is scoped under
 *      `/organizationSettings/:id` (the org id). There is no shallow `/<family>/<tab>`
 *      static-family level, so there is no family/entity split — just one entity page.
 *   2. The family is SIDENAV-driven (`SidenavContentType.Settings`), NOT a horizontal
 *      tab bar. So the page mirrors the ACCOUNTS "no-FamilyLayout-tab-bar" choice: it
 *      owns the nested <Switch> but renders NO Subnav tab bar at the org level (the
 *      persistent settings Sidenav, mounted in the Authed shell, is the navigation).
 *   3. The family nests deeper than one level (`/expensecategories/custom/:customFieldId`,
 *      `/expensecategories/:expenseCategoryId`, `/departments/:departmentId`). The flat
 *      gate matcher is one-param-per-segment, so each concrete depth is ENUMERATED here
 *      explicitly rather than collapsed into a single `:tab` param-child matcher.
 *
 * Card Feeds (`/cardfeeds*`) live OUTSIDE `/organizationSettings` but are part of the
 * organization-settings family (their appRoutes live in appRoutes/organization, their
 * AppLocations are `OrganizationSettingsCardFeed*`), so they are migrated here too.
 *
 * OUT OF LANE (stay legacy, overlay pillar EX-39749 — NOT in this tree):
 *   - `/organizationSettings/:id/integrations/:integrationId/manage` (ManageOrgIntegration popup)
 *   - `/organizationSettings/:id/cancelsubscription` (CancelSubscription popup)
 *
 * `/organizationSettings/:id/rules` and `/organizationSettings/:id/expenseSettings` render
 * the in-shell not-found (they exist only inside AutomationSettings' nested <Switch> at
 * `/automation`, with no renderable top-level entry). They ARE still enumerated in `settings`
 * — not as renderable tabs, but so locationHooks resolves their AppLocation for the settings
 * sidenav "Automation" active state. See the note above those two nodes.
 */

export interface OrganizationRouteNode {
  /** Stable key used for tests + readability. */
  key: string
  /** AppLocation that drives sidenav active state via locationHooks. */
  appLocation: AppLocation
  /**
   * Exact URL pattern. Param names (`:id` vs `:orgId`, `:expenseCategoryId`,
   * `:customFieldId`, `:departmentId`) are kept VERBATIM from the legacy routeProps so
   * locationHooks resolves the same params the page selectors already read.
   */
  path: string
}

export const organizationRouteTree = {
  entity: {
    /** `/organizationSettings/:id` — the organization-settings entity root (org id = `:id`). */
    rootPath: Routes.organizationSettingsUrl(':id'),
    rootParam: 'id',
  },
  /**
   * Settings routes under `/organizationSettings/:id/*`. Ordered most-specific first so
   * this list reads like — and the page's nested <Switch> mirror preserves — the legacy
   * Authed background <Switch> precedence. (The flat gate specificity-sorts independently,
   * so ordering here is for readability and the page mirror, not gate correctness.)
   */
  settings: [
    {
      key: 'customFieldDetail',
      appLocation: AppLocation.OrganizationSettingsCustomFieldDetail,
      path: Routes.customFieldDetailUrl(':orgId', ':customFieldId'),
    },
    {
      key: 'expenseCategoryLabels',
      appLocation: AppLocation.OrganizationSettingsExpenseCategoryLabels,
      path: Routes.expenseCategoryLabelsUrl(':orgId', ':expenseCategoryId'),
    },
    {
      key: 'expenseCategoriesCustom',
      appLocation:
        AppLocation.OrganizationSettingsExpenseCategoriesAdditionalFields,
      path: Routes.expenseCategoriesAdditionalFieldsUrl(':id'),
    },
    {
      key: 'expenseCategoriesAccount',
      appLocation: AppLocation.OrganizationSettingsExpenseCategories,
      path: Routes.expenseCategoriesAccountingUrl(':id'),
    },
    {
      key: 'expenseCategories',
      appLocation: AppLocation.OrganizationSettingsExpenseCategories,
      path: Routes.expenseCategoriesUrl(':id'),
    },
    {
      key: 'departmentDetails',
      appLocation: AppLocation.DepartmentDetails,
      path: Routes.departmentDetailsUrl(':orgId', ':departmentId'),
    },
    {
      key: 'departments',
      appLocation: AppLocation.OrganizationSettingsDepartments,
      path: Routes.departmentsUrl(':id'),
    },
    {
      key: 'integrations',
      appLocation: AppLocation.OrganizationSettingsIntegrations,
      path: Routes.organizationIntegrationsUrl(':id'),
    },
    {
      key: 'businessRules',
      appLocation: AppLocation.OrganizationSettingsBusinessRules,
      path: Routes.businessRulesUrl(':id'),
    },
    {
      key: 'expenseRequirementsReceiptRules',
      appLocation:
        AppLocation.OrganizationSettingsExpenseRequirementsReceiptRules,
      path: Routes.expenseRequirementsReceiptRulesUrl(':id'),
    },
    {
      key: 'expenseRequirementsAdditionalFields',
      appLocation:
        AppLocation.OrganizationSettingsExpenseRequirementsAdditionalFields,
      path: Routes.expenseRequirementsAdditionalFieldsUrl(':id'),
    },
    {
      key: 'expenseRequirements',
      appLocation: AppLocation.OrganizationSettingsExpenseRequirements,
      path: Routes.expenseRequirementsUrl(':id'),
    },
    {
      key: 'reimbursements',
      appLocation: AppLocation.OrganizationSettingsReimbursements,
      path: Routes.orgSettingsReimbursementsUrl(':id'),
    },
    {
      key: 'organizationInfo',
      appLocation: AppLocation.OrganizationSettingsOrganizationInfo,
      path: Routes.organizationInfoUrl(':id'),
    },
    {
      key: 'bankAccounts',
      appLocation: AppLocation.OrganizationSettingsBankAccounts,
      path: Routes.bankAccountsUrl(':id'),
    },
    {
      key: 'manageSubscription',
      appLocation: AppLocation.OrganizationSettingsManageSubscription,
      path: Routes.manageSubscriptionUrl(':id'),
    },
    {
      key: 'sso',
      appLocation: AppLocation.OrganizationSettingsSSO,
      path: Routes.organizationSsoUrl(':id'),
    },
    {
      key: 'apiKeys',
      appLocation: AppLocation.OrganizationSettingsAPIKeys,
      path: Routes.apiKeysUrl(':id'),
    },
    {
      key: 'automation',
      appLocation: AppLocation.OrganizationSettingAutomationSettings,
      path: Routes.automationSettingsUrl(':id'),
    },
    // `/rules` + `/expenseSettings` have NO top-level registry entry today — they exist
    // only inside AutomationSettings' own nested <Switch> (which mounts at /automation), so
    // they render the in-shell not-found. They ARE enumerated here (not as renderable tabs)
    // SOLELY so locationHooks still resolves their AppLocation — the settings sidenav
    // "Automation" item keys its active state on OrganizationSettingsAutomationRules /
    // OrganizationSettingsExpenseSettings (see useSettingsNavItems.tsx). Omitting them would
    // regress that highlight; this preserves it exactly. (Pre-existing not-found rendering
    // is preserved, not "fixed" — that wiring is out of scope for this routing migration.)
    {
      key: 'automationRules',
      appLocation: AppLocation.OrganizationSettingsAutomationRules,
      path: Routes.automationRulesUrl(':id'),
    },
    {
      key: 'expenseSettings',
      appLocation: AppLocation.OrganizationSettingsExpenseSettings,
      path: Routes.expenseSettingsUrl(':id'),
    },
  ] as OrganizationRouteNode[],
  /**
   * Card Feeds: top-level `/cardfeeds*` routes that belong to the org-settings family.
   * `create` precedes `:id` so the static path out-specifies the param at equal depth.
   */
  cardFeeds: [
    {
      key: 'cardFeedCreate',
      appLocation: AppLocation.OrganizationSettingsCardFeedCreate,
      path: Routes.createCardFeedUrl(),
    },
    {
      key: 'cardFeedDetails',
      appLocation: AppLocation.OrganizationSettingsCardFeedDetails,
      path: Routes.cardFeedDetailsUrl(':id'),
    },
    {
      key: 'cardFeeds',
      appLocation: AppLocation.OrganizationSettingsCardFeeds,
      path: Routes.cardFeedsUrl(),
    },
  ] as OrganizationRouteNode[],
}

/**
 * Entity-root node for `/organizationSettings/:id`. The legacy registry made
 * AutomationSettings the catch-all at this base path, so the bare root resolved to
 * `OrganizationSettingAutomationSettings` + `SidenavContentType.Settings`. locationHooks
 * matches flat metadata with `exact:true`, so without this entry the bare root would fall
 * back to `AppLocation.Home` + `SidenavContentType.Main` (wrong sidenav context) even though
 * the gate still routes it to OrganizationSettingsPage. This preserves the legacy resolution.
 */
const entityRootNode: OrganizationRouteNode = {
  key: 'entityRoot',
  appLocation: AppLocation.OrganizationSettingAutomationSettings,
  path: organizationRouteTree.entity.rootPath,
}

/** Every in-lane organization route node, in family order (root, settings, then card feeds). */
export const organizationRouteNodes: OrganizationRouteNode[] = [
  entityRootNode,
  ...organizationRouteTree.settings,
  ...organizationRouteTree.cardFeeds,
]

/**
 * GATE paths for the organization-settings page. Unlike the metadata (one entry per concrete
 * URL, above), the GATE routes the WHOLE `/organizationSettings/:id` subtree to the single
 * OrganizationSettingsPage via param-child matchers — mirroring the Budgets/Accounts entity
 * gate (`/budgets/:id/:budgetTab`). This is required because the legacy registry made
 * AutomationSettings the org-settings CATCH-ALL (its routeProps is the base
 * `/organizationSettings/:id`, exact:false), so EVERY URL under the org-settings root — known
 * tab, deeper detail, or unknown child — must reach OrganizationSettingsPage, whose nested
 * <Switch> then routes precisely (the catch-all renders its own in-page not-found). Matchers
 * cover depths 0–3 below the entity root (2–5 path segments); the deepest in-lane URL is
 * `/expensecategories/custom/:customFieldId` (5 segments). The `:id`/`:s*` param names are
 * arbitrary here (the gate only needs structural matching; concrete params resolve from the
 * per-path metadata in locationHooks). Card Feeds (`/cardfeeds*`) are enumerated separately.
 */
export const organizationGatePaths: string[] = [
  organizationRouteTree.entity.rootPath, // /organizationSettings/:id
  `${organizationRouteTree.entity.rootPath}/:orgSettingsTab1`,
  `${organizationRouteTree.entity.rootPath}/:orgSettingsTab1/:orgSettingsTab2`,
  `${organizationRouteTree.entity.rootPath}/:orgSettingsTab1/:orgSettingsTab2/:orgSettingsTab3`,
  Routes.createCardFeedUrl(), // /cardfeeds/create (static — before /:id)
  Routes.cardFeedDetailsUrl(':id'), // /cardfeeds/:id
  Routes.cardFeedsUrl(), // /cardfeeds
]
